import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Camera, Check, ChevronDown, ChevronUp, ImageOff, Lock, Mountain, MapPin } from "lucide-react";
import {
  BULK_CHECKIN_MATCH_CHUNK,
  buildBulkReview,
  bulkCheckinClientId,
  bulkSummary,
  bulkUnmatchedReason,
  defaultBulkSelection,
  rowTarget,
  type BulkPhotoInput,
  type BulkReview,
  type BulkReviewRow,
} from "@atlas/shared";
import { Button } from "../components/Button";
import { Badge } from "../components/Badge";
import { useAuth } from "../hooks/useAuth";
import { readExifCapture } from "../lib/exif";
import {
  fetchCheckinUploadUrl,
  postCheckinBatchFinish,
  postCheckinPhotoMatch,
  postLandmarkCheckin,
  postPeakCheckin,
} from "../api/landmarks";

// Bulk photo check-ins (spec 39 / ATL-166). Pick many summit photos; the
// browser reads each one's location and date, the server says what each was
// taken at, the user reviews, and only the chosen photos upload, one ordinary
// check-in each. Unmatched photos never leave the computer.

const MAX_UPLOAD_EDGE = 2400;
const MAX_PICK = 1000;

interface PickedPhoto {
  ref: string;
  file: File;
  url: string;
  lat: number | null;
  lon: number | null;
  takenAt: string | null;
}

type Stage =
  | { kind: "pick" }
  | { kind: "reading"; done: number; total: number }
  | { kind: "matching" }
  | { kind: "review" }
  | { kind: "uploading"; done: number; total: number }
  | { kind: "done"; added: number; newHills: number; failed: { name: string; reason: string }[] };

function formatDay(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!)).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatDistance(m: number): string {
  return `${Math.round(m)} m`;
}

/** Downscale to a JPEG the upload accepts. Falls back to the original file
 * when the browser can't decode it (HEIC outside Safari). */
async function uploadBody(file: File): Promise<{ blob: Blob; type: string; width: number | null; height: number | null }> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_UPLOAD_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no canvas");
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
    if (!blob) throw new Error("encode failed");
    return { blob, type: "image/jpeg", width, height };
  } catch {
    if (["image/jpeg", "image/png", "image/webp"].includes(file.type) && file.size <= 5 * 1024 * 1024) {
      return { blob: file, type: file.type, width: null, height: null };
    }
    throw new Error("This browser can't read this photo. Save it as a JPEG and try again.");
  }
}

export default function PhotoImportPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [stage, setStage] = React.useState<Stage>({ kind: "pick" });
  const [error, setError] = React.useState<string | null>(null);
  const [photos, setPhotos] = React.useState<Map<string, PickedPhoto>>(new Map());
  const [review, setReview] = React.useState<BulkReview | null>(null);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [chosen, setChosen] = React.useState<Map<string, string>>(new Map());
  const [expanded, setExpanded] = React.useState<string | null>(null);
  const [makePublic, setMakePublic] = React.useState(false);
  const [historyCutoff, setHistoryCutoff] = React.useState<string | null>(null);

  // Free the object URLs when leaving the page.
  const photosRef = React.useRef(photos);
  photosRef.current = photos;
  React.useEffect(
    () => () => {
      for (const p of photosRef.current.values()) URL.revokeObjectURL(p.url);
    },
    [],
  );

  function reset() {
    for (const p of photos.values()) URL.revokeObjectURL(p.url);
    setPhotos(new Map());
    setReview(null);
    setSelected(new Set());
    setChosen(new Map());
    setExpanded(null);
    setError(null);
    setStage({ kind: "pick" });
  }

  async function handleFiles(list: FileList | File[]) {
    const files = Array.from(list).filter((f) => f.type.startsWith("image/") || /\.(heic|heif|jpe?g|png|webp)$/i.test(f.name));
    if (files.length === 0) return;
    if (files.length > MAX_PICK) {
      setError(`Pick up to ${MAX_PICK} photos at a time.`);
      return;
    }
    setError(null);
    const picked = new Map<string, PickedPhoto>();
    setStage({ kind: "reading", done: 0, total: files.length });
    for (let i = 0; i < files.length; i++) {
      const file = files[i]!;
      const exif = await readExifCapture(file);
      const ref = `p${i}`;
      picked.set(ref, {
        ref,
        file,
        url: URL.createObjectURL(file),
        lat: exif.lat,
        lon: exif.lon,
        takenAt: exif.takenAt ? exif.takenAt.toISOString() : null,
      });
      if (i % 5 === 4) setStage({ kind: "reading", done: i + 1, total: files.length });
    }
    setPhotos(picked);
    setStage({ kind: "matching" });
    try {
      const inputs: BulkPhotoInput[] = [...picked.values()].map((p) => ({
        ref: p.ref,
        lat: p.lat,
        lon: p.lon,
        takenAt: p.takenAt,
      }));
      const matches = [];
      let cutoff: string | null = null;
      for (let i = 0; i < inputs.length; i += BULK_CHECKIN_MATCH_CHUNK) {
        const res = await postCheckinPhotoMatch(inputs.slice(i, i + BULK_CHECKIN_MATCH_CHUNK));
        matches.push(...res.matches);
        cutoff = res.historyCutoff;
      }
      const built = buildBulkReview(
        matches,
        new Map([...picked.values()].map((p) => [p.ref, p.takenAt])),
      );
      setHistoryCutoff(cutoff);
      setReview(built);
      setSelected(defaultBulkSelection(built.rows));
      setChosen(new Map(built.rows.map((r) => [r.key, r.chosenRef])));
      setStage({ kind: "review" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't check these photos. Try again.");
      setStage({ kind: "pick" });
    }
  }

  async function confirm() {
    if (!review || !user) return;
    const rows = review.rows.filter((r) => selected.has(r.key));
    if (rows.length === 0) return;
    const batchId = crypto.randomUUID();
    const failed: { name: string; reason: string }[] = [];
    const succeeded = new Set<string>();
    let added = 0;
    setStage({ kind: "uploading", done: 0, total: rows.length });
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]!;
      const photo = photos.get(chosen.get(row.key) ?? row.chosenRef);
      try {
        if (!photo || photo.lat === null || photo.lon === null || !photo.takenAt) throw new Error("Photo missing");
        const body = await uploadBody(photo.file);
        const { uploadUrl, key } = await fetchCheckinUploadUrl(body.type);
        const put = await fetch(uploadUrl, { method: "PUT", body: body.blob, headers: { "Content-Type": body.type } });
        if (!put.ok) throw new Error("Photo upload failed");
        const target = rowTarget(row, photo.ref);
        const post = target.kind === "peak" ? postPeakCheckin : postLandmarkCheckin;
        await post(target.slug, {
          clientId: bulkCheckinClientId(user.id, row),
          photoKey: key,
          width: body.width ?? undefined,
          height: body.height ?? undefined,
          checkedInAt: photo.takenAt,
          exifLat: photo.lat,
          exifLon: photo.lon,
          isPrivate: !makePublic,
          batchId,
        });
        added += 1;
        succeeded.add(row.key);
      } catch (err) {
        failed.push({
          name: `${row.peak?.name ?? row.target.name}, ${formatDay(row.visitDay)}`,
          reason: err instanceof Error ? err.message : "Couldn't add this one",
        });
      }
      setStage({ kind: "uploading", done: i + 1, total: rows.length });
    }
    if (added > 0) {
      await postCheckinBatchFinish(batchId).catch(() => {});
      void queryClient.invalidateQueries();
    }
    setStage({ kind: "done", added, newHills: bulkSummary(rows, succeeded).newHills, failed });
  }

  // Leaving mid-upload skips the batch's finish step; ask first.
  const uploading = stage.kind === "uploading";
  React.useEffect(() => {
    if (!uploading) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploading]);

  const summary = review ? bulkSummary(review.rows, selected) : null;

  return (
    <div>
      <div className="r-page-x" style={{ padding: "28px 28px 16px" }}>
        <p className="eyebrow" style={{ fontSize: 11, marginBottom: 4 }}>Your data</p>
        <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: 0 }}>Add from photos</h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 6, marginBottom: 0, maxWidth: 640 }}>
          For walks without a tracker: pick your summit photos and Atlas finds the hills. A photo counts
          when it was taken within 75 m of the top.
        </p>
      </div>

      <div className="r-page-x" style={{ padding: "0 28px 28px", display: "flex", flexDirection: "column", gap: 16, maxWidth: 860 }}>
        {error && (
          <div role="alert" style={{ fontSize: 13, color: "var(--status-danger)" }}>{error}</div>
        )}

        {stage.kind === "pick" && (
          <Card>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (e.dataTransfer.files.length) void handleFiles(e.dataTransfer.files);
              }}
              style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" }}
            >
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <IconTile><Camera size={18} /></IconTile>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Choose photos</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                    Or drag them onto this card. Atlas checks each photo's location and date first;
                    nothing uploads until you confirm.
                  </div>
                </div>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,.heic,.heif"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files?.length) void handleFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <Button onClick={() => fileRef.current?.click()}>Choose photos</Button>
              <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
                Photos need their location saved. Edited copies, scans and photos sent through
                messaging apps often lose it; the originals from your camera or phone usually keep it.
              </p>
            </div>
          </Card>
        )}

        {stage.kind === "reading" && (
          <Card><Progress label={`Reading photos, ${stage.done} of ${stage.total}`} value={stage.done / stage.total} /></Card>
        )}
        {stage.kind === "matching" && (
          <Card><Progress label="Finding the hills" value={null} /></Card>
        )}

        {stage.kind === "review" && review && summary && (
          <>
            <Card>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div className="tnum" style={{ font: "var(--type-h3)", color: "var(--text-primary)" }}>
                    {summary.hills} {summary.hills === 1 ? "hill" : "hills"}
                    {summary.places > 0 ? ` and ${summary.places} ${summary.places === 1 ? "place" : "places"}` : ""}
                    <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> from {photos.size} photos</span>
                  </div>
                  <div style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 2 }}>
                    {summary.newHills} new to your record. Only the ticked photos upload.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button variant="ghost" onClick={reset}>Start again</Button>
                  <Button disabled={summary.checkins === 0} onClick={() => void confirm()}>
                    {summary.checkins === 0 ? "Nothing ticked" : `Add ${summary.checkins} ${summary.checkins === 1 ? "check-in" : "check-ins"}`}
                  </Button>
                </div>
              </div>
              <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: "var(--text-secondary)", marginTop: 12 }}>
                <input type="checkbox" checked={makePublic} onChange={(e) => setMakePublic(e.target.checked)} />
                Show these photos in each hill's public gallery (they stay private otherwise; you can change any of them later)
              </label>
            </Card>

            {review.rows.length > 0 ? (
              <Card padding={0}>
                {review.rows.map((row, i) => (
                  <ReviewRow
                    key={row.key}
                    row={row}
                    first={i === 0}
                    photos={photos}
                    ticked={selected.has(row.key)}
                    chosenRef={chosen.get(row.key) ?? row.chosenRef}
                    expanded={expanded === row.key}
                    onToggle={() =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (next.has(row.key)) next.delete(row.key);
                        else next.add(row.key);
                        return next;
                      })
                    }
                    onExpand={() => setExpanded((prev) => (prev === row.key ? null : row.key))}
                    onChoose={(ref) => setChosen((prev) => new Map(prev).set(row.key, ref))}
                  />
                ))}
              </Card>
            ) : (
              <Card>
                <div style={{ fontSize: 14, color: "var(--text-secondary)" }}>
                  None of these photos were taken within 75 m of a summit or landmark Atlas knows.
                </div>
              </Card>
            )}

            {review.locked.length > 0 && (
              <Card>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <Lock size={16} style={{ color: "var(--text-muted)", marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
                      {review.locked.length} older {review.locked.length === 1 ? "summit" : "summits"}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 2 }}>
                      The free plan records the last 12 months
                      {historyCutoff ? `, from ${new Date(historyCutoff).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}` : ""}.
                      These were matched but stay on your phone or computer.{" "}
                      <Link to="/plan" style={{ color: "var(--text-primary)" }}>Full history</Link> adds them.
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>
                      {review.locked.map((r) => `${r.peak?.name ?? r.target.name} (${formatDay(r.visitDay)})`).join(", ")}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {review.unmatched.length > 0 && (
              <Card padding={0}>
                <div style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                  {review.unmatched.length} {review.unmatched.length === 1 ? "photo" : "photos"} not matched
                  <span style={{ fontWeight: 400, color: "var(--text-muted)" }}> · never uploaded</span>
                </div>
                {review.unmatched.map((u) => {
                  const p = photos.get(u.ref);
                  return (
                    <div key={u.ref} style={{ display: "flex", gap: 12, alignItems: "center", padding: "8px 16px", borderTop: "1px solid var(--border-subtle)" }}>
                      <Thumb url={p?.url} size={36} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13, color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p?.file.name}</div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{bulkUnmatchedReason(u.status)}</div>
                      </div>
                    </div>
                  );
                })}
              </Card>
            )}
          </>
        )}

        {stage.kind === "uploading" && (
          <Card><Progress label={`Adding check-ins, ${stage.done} of ${stage.total}`} value={stage.done / stage.total} /></Card>
        )}

        {stage.kind === "done" && (
          <Card>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <IconTile>{stage.added > 0 ? <Check size={18} /> : <ImageOff size={18} />}</IconTile>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ font: "var(--type-h3)", color: "var(--text-primary)" }}>
                  {stage.added === 0
                    ? "Nothing was added"
                    : `${stage.added} ${stage.added === 1 ? "check-in" : "check-ins"} added`}
                </div>
                {stage.newHills > 0 && (
                  <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                    {stage.newHills} new {stage.newHills === 1 ? "hill" : "hills"} on your record.
                  </div>
                )}
                {stage.failed.length > 0 && (
                  <div style={{ fontSize: 13, color: "var(--status-danger)" }}>
                    {stage.failed.length} didn't go through:
                    <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                      {stage.failed.map((f) => <li key={f.name}>{f.name}: {f.reason}</li>)}
                    </ul>
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                  <Link to="/collections"><Button variant="secondary">See your collections</Button></Link>
                  <Button variant="ghost" onClick={reset}>Add more photos</Button>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function ReviewRow({
  row,
  first,
  photos,
  ticked,
  chosenRef,
  expanded,
  onToggle,
  onExpand,
  onChoose,
}: {
  row: BulkReviewRow;
  first: boolean;
  photos: Map<string, PickedPhoto>;
  ticked: boolean;
  chosenRef: string;
  expanded: boolean;
  onToggle: () => void;
  onExpand: () => void;
  onChoose: (ref: string) => void;
}) {
  const chosenPhoto = row.photos.find((p) => p.ref === chosenRef) ?? row.photos[0]!;
  const many = row.photos.length > 1;
  return (
    <div style={{ borderTop: first ? "none" : "1px solid var(--border-subtle)" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", padding: "10px 16px" }}>
        <input
          type="checkbox"
          checked={ticked}
          onChange={onToggle}
          aria-label={`Add ${row.peak?.name ?? row.target.name}, ${formatDay(row.visitDay)}`}
        />
        <Thumb url={photos.get(chosenRef)?.url} size={52} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {row.peak ? <Mountain size={14} style={{ color: "var(--text-muted)" }} /> : <MapPin size={14} style={{ color: "var(--text-muted)" }} />}
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{row.peak?.name ?? row.target.name}</span>
            {row.peak && <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)" }}>{row.peak.elevationM} m</span>}
            {row.alreadyCheckedIn ? (
              <Badge>Already checked in that day</Badge>
            ) : row.alreadyCollected ? (
              <Badge>On your record</Badge>
            ) : (
              <Badge variant="info">New</Badge>
            )}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            {formatDay(row.visitDay)} · {formatDistance(chosenPhoto.distanceM)} from the {row.peak ? "top" : "point"}
            {row.peakLocked ? " · the summit needs Full history" : ""}
          </div>
          {many && (
            <button
              type="button"
              onClick={onExpand}
              aria-expanded={expanded}
              style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 4, padding: 0, border: "none", background: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}
            >
              {row.photos.length} photos, using this one
              {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
          )}
        </div>
      </div>
      {many && expanded && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "0 16px 12px 16px" }}>
          {row.photos.map((p) => (
            <button
              key={p.ref}
              type="button"
              onClick={() => onChoose(p.ref)}
              aria-pressed={p.ref === chosenRef}
              title={`${formatDistance(p.distanceM)} from the top`}
              style={{
                padding: 0,
                borderRadius: "var(--radius-md)",
                border: `2px solid ${p.ref === chosenRef ? "var(--border-gold)" : "transparent"}`,
                background: "none",
                cursor: "pointer",
              }}
            >
              <Thumb url={photos.get(p.ref)?.url} size={64} />
            </button>
          ))}
          <div style={{ fontSize: 12, color: "var(--text-muted)", alignSelf: "center" }}>
            One photo per hill per day. The others stay where they are.
          </div>
        </div>
      )}
    </div>
  );
}

function Thumb({ url, size }: { url: string | undefined; size: number }) {
  const [broken, setBroken] = React.useState(false);
  const box: React.CSSProperties = {
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: "var(--radius-md)",
    background: "var(--surface-sunken)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    color: "var(--text-muted)",
  };
  if (!url || broken) return <span style={box}><ImageOff size={16} /></span>;
  return (
    <span style={box}>
      <img src={url} alt="" onError={() => setBroken(true)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
    </span>
  );
}

function Card({ children, padding = 18 }: { children: React.ReactNode; padding?: number }) {
  return (
    <div
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--ring-top)",
        padding,
        minWidth: 0,
      }}
    >
      {children}
    </div>
  );
}

function IconTile({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: "var(--radius-md)",
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-subtle)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--text-muted)",
      }}
    >
      {children}
    </span>
  );
}

function Progress({ label, value }: { label: string; value: number | null }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div className="tnum" style={{ fontSize: 14, color: "var(--text-secondary)" }}>{label}</div>
      <div style={{ height: 6, borderRadius: 3, background: "var(--surface-sunken)", overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            width: value === null ? "40%" : `${Math.round(value * 100)}%`,
            background: "var(--status-info)",
            transition: "width 200ms",
          }}
        />
      </div>
    </div>
  );
}
